import React from 'react';
import { Box, Stack, TextField, MenuItem } from '@mui/material';
import { CategoryOutlined } from '@mui/icons-material';
import { SectionHeader, SectionPaper } from '../../../../common/components/styled-ui-elements';
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks';
import { updateComplianceIdentification } from '../../../state';
import {
  useGetComplianceTypesQuery,
  useGetAccreditationBodiesQuery,
} from '../../../../../services/DimensionService/dimensionApi';
import { EnumView } from '../../../../../services/DimensionService/types/dimensionTypes';
import { ComplianceItemView } from '../../../../../services/ComplianceService/types/complianceTypes';

type FieldChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => void;

/**
 * Resolves every identification field to its display value (defaulting to '') in one place, so the
 * section component itself has no optional-chaining/fallback branches of its own. Uses destructuring
 * defaults (rather than a `?.`/`||` fallback per field) so this stays a single guard clause instead
 * of one branch per field.
 */
const resolveIdentificationFields = (item: ComplianceItemView | null) => {
  const {
    title = '',
    complianceType = '',
    accreditationBody = '',
    standard = '',
    requirement = '',
    category = '',
    description = '',
  } = item || {};

  return { title, complianceType, accreditationBody, standard, requirement, category, description };
};

/** A required select field backed by a dimensional EnumView[] list, e.g. Compliance Type. */
const IdentificationDropdownField: React.FC<{
  label: string;
  placeholder: string;
  value: string;
  options: EnumView[];
  onChange: FieldChangeHandler;
}> = ({ label, placeholder, value, options, onChange }) => (
  <TextField label={label} size="small" required select fullWidth value={value} onChange={onChange}>
    <MenuItem value="">
      <em>{placeholder}</em>
    </MenuItem>
    {options.map((option) => (
      <MenuItem key={option.value} value={option.value}>
        {option.name}
      </MenuItem>
    ))}
  </TextField>
);

/** The Standard/Requirement/Category row - free-text detail fields, no dropdowns. */
const IdentificationDetailFields: React.FC<{
  standard: string;
  requirement: string;
  category: string;
  onFieldChange: (field: string) => FieldChangeHandler;
}> = ({ standard, requirement, category, onFieldChange }) => (
  <Stack direction="row" gap={2}>
    <TextField label="Standard" size="small" fullWidth value={standard} onChange={onFieldChange('standard')} />
    <TextField label="Requirement" size="small" fullWidth value={requirement} onChange={onFieldChange('requirement')} />
    <TextField label="Category" size="small" fullWidth value={category} onChange={onFieldChange('category')} />
  </Stack>
);

/**
 * IdentificationSection - Compliance identification fields
 * Contains title, description, compliance type, accreditation body, standard, requirement, category
 */
export const IdentificationSection: React.FC = () => {
  const dispatch = useAppDispatch();
  const { currentComplianceItem } = useAppSelector(state => state.compliance);
  const { data: complianceTypes = [] } = useGetComplianceTypesQuery();
  const { data: accreditationBodies = [] } = useGetAccreditationBodiesQuery();
  const fields = resolveIdentificationFields(currentComplianceItem);

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(updateComplianceIdentification({ [field]: event.target.value }));
  };

  return (
    <Box>
      <SectionHeader
        icon={<CategoryOutlined sx={{ mr: 1, color: '#3b82f6', fontSize: 18 }} />}
        title="Identification"
      />
      <SectionPaper>
        <Stack spacing={2}>
          <Stack direction="row" gap={2}>
            <TextField
              label="Title"
              size="small"
              required
              fullWidth
              value={fields.title}
              onChange={handleChange('title')}
            />
            <IdentificationDropdownField
              label="Compliance Type"
              placeholder="Select Type"
              value={fields.complianceType}
              options={complianceTypes}
              onChange={handleChange('complianceType')}
            />
            <IdentificationDropdownField
              label="Accreditation Body"
              placeholder="Select Body"
              value={fields.accreditationBody}
              options={accreditationBodies}
              onChange={handleChange('accreditationBody')}
            />
          </Stack>
          <IdentificationDetailFields
            standard={fields.standard}
            requirement={fields.requirement}
            category={fields.category}
            onFieldChange={handleChange}
          />
          <TextField
            label="Description"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={fields.description}
            onChange={handleChange('description')}
            InputProps={{
              sx: {
                alignItems: 'flex-start',
                '& .MuiInputBase-input': {
                  paddingTop: '8px',
                }
              }
            }}
          />
        </Stack>
      </SectionPaper>
    </Box>
  );
};

export default IdentificationSection;
